/* "Before you check out" agreement (David, 2026-10-14). Nobody reaches Shopify's checkout without ticking the box in
   snippets/pranos-terms.liquid:
   - Cart: Check out keeps its normal look; a tap before the tick points to the box instead of doing nothing (David: it
     must never look broken): the card turns blue, the checkbox gets a ring and focus, and one red line says what to do.
     The box is also a required field of the cart form, so the browser holds an unticked Check out even before this
     script runs.
   - Product page: Buy Now (which skips the cart) opens the same box in a dialog first. An inline guard in
     layout/theme.liquid holds a Buy Now or Check out tap made before this script is ready, and start() replays it.
     Back to a cached page (or back to the tab), the cart's record is read again before Buy Now trusts it.
   Ticking saves "Agreed to terms" (with the date and time) and the exact points on the cart, so every order keeps them
   (admin: Additional details). Unticking removes them. Express-pay buttons are off in this theme, so these are the only
   two ways into checkout from the site. Only an agreement to the current points (version 2026-10-24) counts; an
   older record ticks again.
   2026-10-19 (iPhone review, then a pre-push review): compact layout (the 5 points in a native <details>; the browser
   opens and closes it, this script never does). In the cart, the agreement also rides with Check out as hidden
   attributes[...] fields of the cart form, filled and enabled only from a tick made on this page or a saved current
   record, so Check out never waits and an order keeps the record even if the background save failed. Buy Now waits
   for a confirmed save (at most 2.5 seconds, one retry) and says so if it still fails.
   Inside the theme's cart this script only switches classes, the checkbox and hidden input values/disabled, never
   text or elements (Horizon rebuilds its refs when nodes change there; see handbook 05 section 3.1). */
(() => {
  const KEY = 'Agreed to terms';
  const POINTS_KEY = 'Terms points agreed to';
  // Same six points, same words as version 2026-10-24 (6th point, content in public: David, 2026-10-24); the record also says the layout they were shown in.
  const POINTS = 'Version 2026-10-24 (compact box, points one tap away, 2026-10-24): play video only while parked and switch the Pranos app to Driving (a still picture) before the car moves, checking local rules first because some places limit even a still picture while driving; Front Windshield for parked use only; keep the views you need clear, HoloGlass does not replace mirrors, cameras or other vehicle systems; back seat empty while HoloGlass is mounted: before anyone rides in the back, take the HoloGlass unit down (it sits near head height); show only what is suitable for all ages in public, nothing explicit, threatening or harassing (free speech has limits; check the laws where you are); check and follow local rules for window displays; agree to the Terms of Service.';
  const SAVE_WAIT_MS = 2500;
  const CHAIN_WAIT_MS = 800; // a save waits this long at most for the one before it (a stuck request can't block later ones)
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let agreed = window.paTermsAgreed === true; // a current record already on the cart (from Liquid)
  let agreedValue = ''; // "Yes, <date time zone>" from a tick on this page
  let savedOk = agreed; // true only once Shopify confirmed the current agreement is saved
  let saving = null; // the latest save request
  let chain = Promise.resolve(); // saves run one after another, so the last action wins
  let pendingBuyNow = null;

  const dialog = () => document.getElementById('pa-terms-dialog');
  // (dateStyle can't be mixed with timeZoneName: the browser throws.)
  const stamp = () => 'Yes, ' + new Date().toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });

  let saveSeq = 0; // counts saves, so a cart re-check started before a save never undoes it
  let savesOpen = 0;

  // keepStamp: a retry sends the same time as the tick, so the record shows when the buyer actually agreed.
  function save(on, keepStamp) {
    if (on && !(keepStamp && agreedValue)) agreedValue = stamp();
    const attributes = on ? { [KEY]: agreedValue, [POINTS_KEY]: POINTS } : { [KEY]: '', [POINTS_KEY]: '' };
    savedOk = false;
    saveSeq++; savesOpen++;
    const request = Promise.race([chain, new Promise((resolve) => setTimeout(resolve, CHAIN_WAIT_MS))]).then(() => fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    }).then((r) => r.ok).catch(() => false)).then((ok) => {
      if (on && ok && agreed && request === saving) savedOk = true; // a save that lands late still counts
      return ok;
    }).finally(() => { savesOpen--; });
    chain = request.catch(() => false);
    saving = request;
    return request;
  }

  // Back to a page kept in the back/forward cache, or back to this tab: the cart may have changed since this page
  // loaded (an order was placed and the cart is new, or the box was unticked on another page). Read the record from
  // the cart again: no current record there means the box shows unticked. Never writes, never invents a record.
  let refreshing = null;
  function refreshFromCart() {
    if (refreshing) return refreshing;
    const seq = saveSeq;
    refreshing = within(fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)))
      .then((cart) => {
        if (!cart || savesOpen > 0 || seq !== saveSeq) return; // unknown, or a save happened meanwhile: keep the state
        const a = cart.attributes || {};
        const current = String(a[KEY] || '').includes('Yes') && String(a[POINTS_KEY] || '').includes('2026-10-24');
        if (current) { if (agreed) savedOk = true; }
        else { agreed = false; agreedValue = ''; savedOk = false; }
      })
      .catch(() => {})
      .then(() => { refreshing = null; sync(); });
    return refreshing;
  }

  // A save that hasn't answered in 2.5 seconds counts as failed for now (a stalled request on a weak connection).
  const within = (p, ms = SAVE_WAIT_MS) => Promise.race([p, new Promise((resolve) => setTimeout(() => resolve(false), ms))]);

  // Before Buy Now opens checkout: the current agreement must be confirmed saved, so the order keeps it. One retry
  // (same time stamp); returns false if both attempts fail, and the caller says so (never a silent failure).
  async function ensureSaved() {
    if (savedOk) return true;
    if (saving && (await within(saving))) return true;
    if (savedOk) return true;
    const ok = await within(save(true, true), SAVE_WAIT_MS + CHAIN_WAIT_MS);
    return ok || savedOk;
  }

  // Hidden attributes[...] fields of the cart form (rendered disabled by the snippet): values and disabled only.
  // Filled from a tick on this page, or kept as the saved current record; never invented, never an older version.
  function setFormAttrs() {
    document.querySelectorAll('input[data-pa-terms-attr]').forEach((input) => {
      let want = '';
      if (agreed && agreedValue) want = input.dataset.paTermsAttr === 'points' ? POINTS : agreedValue;
      else if (agreed) want = input.defaultValue; // the server-rendered current record
      if (input.value !== want) input.value = want;
      const off = !want;
      if (input.disabled !== off) input.disabled = off;
    });
  }

  // Every box and the cart's Check out button follow one state (classes, the checkbox and input values only).
  function sync() {
    // An agreement from an earlier page view with no saved record on this cart (another tab unticked, or the record
    // was lost): show the box unticked, so the buyer ticks again.
    if (agreed && !agreedValue) {
      const field = document.querySelector('input[data-pa-terms-attr="agreed"]');
      if (field && !field.defaultValue) { agreed = false; savedOk = false; }
    }
    document.querySelectorAll('.pa-terms').forEach((box) => {
      if (box.classList.contains('is-agreed') !== agreed) box.classList.toggle('is-agreed', agreed);
      const input = box.querySelector('.pa-terms__input');
      if (input && input.checked !== agreed) input.checked = agreed;
    });
    document.querySelectorAll('.cart__checkout-button').forEach((btn) => {
      if (btn.classList.contains('pa-terms-wait') === agreed) btn.classList.toggle('pa-terms-wait', !agreed);
    });
    setFormAttrs();
  }

  // Points to the box when someone tries to check out first. The 5 points stay as they are (open or closed).
  function nudge(box) {
    if (!box) return;
    sync();
    box.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    box.classList.remove('pa-terms--nudge'); void box.offsetWidth; box.classList.add('pa-terms--nudge', 'pa-terms--msg');
    box.querySelector('.pa-terms__input')?.focus({ preventScroll: true });
  }

  function openDialog(buyNow, failed) {
    const d = dialog();
    if (!d) return;
    pendingBuyNow = buyNow;
    sync();
    // (Older dialog markup rendered Continue disabled; this script never disables it.)
    const go = d.querySelector('[data-pa-terms-go]');
    if (go && go.disabled && go.getAttribute('aria-busy') !== 'true') go.disabled = false;
    d.querySelector('.pa-terms')?.classList.toggle('pa-terms--err', !!failed);
    if (!d.open) d.showModal();
  }

  // On window, capture phase: runs before the theme's own delegated handlers (assets/component.js listens on document
  // in the capture phase), and the cart drawer stops change events from bubbling.
  window.addEventListener('change', (e) => {
    const input = e.target.closest && e.target.closest('.pa-terms__input');
    if (!input) return;
    e.stopPropagation();
    agreed = input.checked;
    if (!agreed) agreedValue = '';
    document.querySelectorAll('.pa-terms--err').forEach((b) => b.classList.remove('pa-terms--err'));
    save(agreed);
    sync();
  }, true);

  // On window, capture phase, so this runs before the theme's handlers (Buy Now would otherwise go straight to checkout).
  window.addEventListener('click', async (e) => {
    const el = e.target.closest && e.target;
    if (!el) return;

    const checkout = el.closest('.cart__checkout-button');
    if (checkout) {
      sync();
      if (!agreed) {
        e.preventDefault(); e.stopImmediatePropagation();
        nudge(document.querySelector('[data-pa-terms="cart"]'));
        return;
      }
      // Agreed: the hidden fields carry the agreement with this submit, so let the form go (no wait, no requestSubmit).
      return;
    }

    const buyNow = el.closest('.pranos-buy-now-button');
    if (buyNow && !buyNow.disabled) {
      // An empty vehicle field: ask for it here (the browser's own message) and stop; the next tap shows the agreement.
      const form = buyNow.closest('product-form-component')?.querySelector('form');
      if (form && !form.checkValidity()) {
        e.preventDefault(); e.stopImmediatePropagation();
        form.reportValidity();
        return;
      }
      // A cart re-check still running (just back to this page): wait for it, then take the tap again.
      if (refreshing) {
        e.preventDefault(); e.stopImmediatePropagation();
        if (buyNow.getAttribute('aria-busy') === 'true') return;
        buyNow.setAttribute('aria-busy', 'true');
        await refreshing;
        buyNow.removeAttribute('aria-busy');
        buyNow.click();
        return;
      }
      sync();
      if (!agreed) {
        if (dialog()) { e.preventDefault(); e.stopImmediatePropagation(); openDialog(buyNow); }
        return;
      }
      if (!savedOk) {
        e.preventDefault(); e.stopImmediatePropagation();
        if (buyNow.getAttribute('aria-busy') === 'true') return;
        buyNow.setAttribute('aria-busy', 'true');
        const ok = await ensureSaved();
        buyNow.removeAttribute('aria-busy');
        if (ok) buyNow.click();
        else openDialog(buyNow, true); // say so, where Continue tries again (never a silent failure)
      }
      return;
    }

    if (el.closest('[data-pa-terms-cancel]')) { dialog()?.close(); return; }

    // The dialog sits outside the theme's components, so its button text can change.
    const go = el.closest('[data-pa-terms-go]');
    if (go) {
      const box = dialog()?.querySelector('.pa-terms');
      if (!agreed) { nudge(box); return; }
      if (go.getAttribute('aria-busy') === 'true') return; // a second tap while saving does nothing
      go.setAttribute('aria-busy', 'true'); go.textContent = 'Opening checkout';
      box?.classList.remove('pa-terms--err');
      const ok = await ensureSaved();
      if (!ok) {
        go.removeAttribute('aria-busy'); go.textContent = 'Continue to checkout';
        box?.classList.add('pa-terms--err');
        return;
      }
      const target = pendingBuyNow;
      dialog()?.close();
      if (target) target.click();
    }
  }, true);

  // Add to cart stays clickable behind the open cart drawer on desktop, so a slow double click added 2 (fresh-eyes test,
  // 2026-10-20). A second Add to cart click within 1.5 seconds, or while the cart drawer is open, does nothing.
  let lastAdd = 0;
  window.addEventListener('click', (e) => {
    const add = e.target.closest && e.target.closest('product-form-component button[type="submit"]');
    if (!add) return;
    const drawerOpen = !!document.querySelector('cart-drawer-component dialog[open]');
    if (Date.now() - lastAdd < 1500 || drawerOpen) { e.preventDefault(); e.stopImmediatePropagation(); return; }
    lastAdd = Date.now();
  }, true);

  // Enter in a cart quantity box (the phone keyboard's Go key) updates that quantity, like leaving the box does, instead
  // of submitting the cart form straight to checkout with a total the buyer never saw (fresh-eyes test, 2026-10-20).
  // Only the event is stopped and the box blurred; the theme's own blur handler updates the cart.
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const input = e.target;
    if (!input || input.tagName !== 'INPUT' || input.name !== 'updates[]') return;
    e.preventDefault();
    let blurred = false;
    input.addEventListener('blur', () => { blurred = true; }, { once: true });
    input.blur();
    if (!blurred) input.dispatchEvent(new FocusEvent('blur')); // a window without focus doesn't fire blur itself
  }, true);

  // Closing the dialog (Cancel, Esc or the back gesture) forgets the pending Buy Now and resets its button.
  document.addEventListener('close', (e) => {
    if (e.target !== dialog()) return;
    pendingBuyNow = null;
    const go = e.target.querySelector('[data-pa-terms-go]');
    if (go) { go.removeAttribute('aria-busy'); go.textContent = 'Continue to checkout'; }
  }, true);

  // Back from checkout (the back/forward cache restores the page as it was): close the dialog if it was left open, and
  // re-check the cart's record. The same re-check runs when the buyer comes back to this tab.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    if (dialog()?.open) dialog().close();
    refreshFromCart();
  });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refreshFromCart(); });

  // The cart drawer re-renders on every change; put the classes, the checkbox and the hidden values back each time.
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; sync(); });
  }).observe(document.documentElement, { childList: true, subtree: true });

  function start() {
    // A tick made in the cart before this deferred script ran counts: keep it instead of undoing it. (autocomplete=off
    // stops the browser restoring an old tick, so a checked box here is a real tap.)
    // The same for an untick: the box was rendered ticked from a saved record, and the buyer unticked it.
    const cartBoxes = [...document.querySelectorAll('[data-pa-terms="cart"] .pa-terms__input')];
    if (!agreed && cartBoxes.some((i) => i.checked)) { agreed = true; save(true); }
    else if (agreed && cartBoxes.length && cartBoxes.some((i) => !i.checked)) { agreed = false; agreedValue = ''; save(false); }
    sync();
    window.paTermsReady = true;
    // A Buy Now or Check out tap held by the inline guard in layout/theme.liquid before this script was ready: carry
    // on with that same button now (a tick made before the script is counted above, so Check out carries it).
    let early = window.paTermsEarlyTap;
    window.paTermsEarlyTap = null;
    if (early === true) early = document.querySelector('.pranos-buy-now-button'); // the guard from before 2026-10-19b
    if (early && early.classList) {
      early.classList.remove('pa-terms-held');
      if (!early.isConnected) early = document.querySelector(early.classList.contains('pranos-buy-now-button') ? '.pranos-buy-now-button' : '.cart__checkout-button');
      if (early && !early.disabled) early.click();
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
